'use client';

import Link from 'next/link';
import { ArrowLeft, Layers, Move3D, Code, Wrench, Lightbulb, Zap } from 'lucide-react';
import StandardLayout from '../../components/layout/StandardLayout';
import '../../components/editor/Documentation.scss';

export default function FundamentalsPage() {
  return (
    <StandardLayout>
      <div className="content-wrapper">
        <div className="docs-container">
          {/* Navigation */}
          <nav className="docs-nav">
            <Link href="/docs" className="back-link">
              <ArrowLeft size={16} />
              Back to Documentation
            </Link>
          </nav>

          {/* Header */}
          <header className="docs-header">
            <h1>Fundamentals</h1>
            <p>Core principles of parametric 3D printing design</p>
          </header>

          {/* Content */}
          <div className="docs-content">
            <section className="docs-section">
              <h2>Why FullControl?</h2>
              <p>
                Traditional 3D printing workflow involves creating a 3D model in CAD software,
                then using a slicer to convert it to G-code. FullControl takes a different approach:
              </p>

              <div className="comparison-grid">
                <div className="comparison-card traditional">
                  <h3>Traditional Workflow</h3>
                  <div className="workflow-steps">
                    <div className="workflow-step">CAD Model</div>
                    <div className="workflow-arrow">→</div>
                    <div className="workflow-step">Slicer</div>
                    <div className="workflow-arrow">→</div>
                    <div className="workflow-step">G-code</div>
                  </div>
                  <ul>
                    <li>Limited control over toolpath</li>
                    <li>Fixed layer heights</li>
                    <li>Standard infill patterns</li>
                    <li>Planar layers only</li>
                  </ul>
                </div>

                <div className="comparison-card fullcontrol">
                  <h3>FullControl Workflow</h3>
                  <div className="workflow-steps">
                    <div className="workflow-step">Python Code</div>
                    <div className="workflow-arrow">→</div>
                    <div className="workflow-step">G-code</div>
                  </div>
                  <ul>
                    <li>Complete toolpath control</li>
                    <li>Variable layer heights</li>
                    <li>Custom infill patterns</li>
                    <li>Non-planar printing</li>
                    <li>Mathematical precision</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="docs-section">
              <h2>Core Principles</h2>

              <div className="principles-grid">
                <div className="principle-card">
                  <Code className="principle-icon" />
                  <h3>Code-First Design</h3>
                  <p>
                    Instead of drawing shapes, you describe them mathematically. This enables
                    precise, repeatable, and parametric designs that can be easily modified.
                  </p>
                  <div className="principle-example">
                    <strong>Example:</strong> A circle isn't drawn—it's defined by radius, center point,
                    and number of segments.
                  </div>
                </div>

                <div className="principle-card">
                  <Move3D className="principle-icon" />
                  <h3>Direct Toolpath Control</h3>
                  <p>
                    You specify exactly where the print head moves, when to extrude, and how fast
                    to move. This gives unprecedented control over the printing process.
                  </p>
                  <div className="principle-example">
                    <strong>Example:</strong> Create custom infill patterns that follow mathematical
                    functions or optimize for specific mechanical properties.
                  </div>
                </div>

                <div className="principle-card">
                  <Layers className="principle-icon" />
                  <h3>Layer Independence</h3>
                  <p>
                    Each layer can have different properties—height, speed, temperature, or even
                    geometry. Layers don't need to be planar or uniform.
                  </p>
                  <div className="principle-example">
                    <strong>Example:</strong> Gradually increase layer height as you go up, or create
                    curved layers for organic shapes.
                  </div>
                </div>

                <div className="principle-card">
                  <Zap className="principle-icon" />
                  <h3>Parametric Design</h3>
                  <p>
                    Use variables, loops, and functions to create designs that can be easily
                    customized. Change one parameter to resize the entire design.
                  </p>
                  <div className="principle-example">
                    <strong>Example:</strong> A parametric box where changing the size variable
                    automatically adjusts all dimensions and infill density.
                  </div>
                </div>
              </div>
            </section>

            <section className="docs-section">
              <h2>Design Philosophy</h2>

              <div className="philosophy-sections">
                <div className="philosophy-item">
                  <h3>🎯 Precision Over Convenience</h3>
                  <p>
                    FullControl prioritizes precise control over ease of use. While it requires more
                    technical knowledge than traditional CAD, it enables designs that are impossible
                    with conventional tools.
                  </p>
                </div>

                <div className="philosophy-item">
                  <h3>🔬 Scientific Approach</h3>
                  <p>
                    Treat 3D printing as a controlled manufacturing process. Every aspect—from
                    material flow to thermal management—can be precisely controlled and optimized.
                  </p>
                </div>

                <div className="philosophy-item">
                  <h3>🔄 Iterative Development</h3>
                  <p>
                    Design, test, measure, and refine. The code-based approach makes it easy to
                    version control your designs and make incremental improvements.
                  </p>
                </div>

                <div className="philosophy-item">
                  <h3>🎨 Creative Freedom</h3>
                  <p>
                    Break free from traditional manufacturing constraints. Create impossible geometries,
                    gradient materials, and structures that adapt to their intended function.
                  </p>
                </div>
              </div>
            </section>

            <section className="docs-section">
              <h2>When to Use FullControl</h2>

              <div className="use-cases">
                <div className="use-case good">
                  <h4>✅ Ideal Use Cases</h4>
                  <ul>
                    <li>Custom infill patterns and structures</li>
                    <li>Non-planar or curved layer printing</li>
                    <li>Parametric designs with many variables</li>
                    <li>Mathematical art and patterns</li>
                    <li>Functional parts with specific properties</li>
                    <li>Research and experimental printing</li>
                    <li>Educational demonstrations</li>
                  </ul>
                </div>

                <div className="use-case consider">
                  <h4>⚖️ Consider Alternatives For</h4>
                  <ul>
                    <li>Simple geometric shapes</li>
                    <li>Quick prototyping</li>
                    <li>Decorative items without functional requirements</li>
                    <li>When you need visual modeling</li>
                    <li>Large, complex assemblies</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="docs-section">
              <h2>Learning Path</h2>
              <p>
                Mastering FullControl is a journey. Here's a recommended learning progression:
              </p>

              <div className="learning-path">
                <div className="learning-stage">
                  <div className="stage-number">1</div>
                  <div className="stage-content">
                    <h4>Foundation</h4>
                    <p>Learn Python basics, understand coordinates, and create simple shapes</p>
                    <div className="stage-skills">
                      <span className="skill">Python syntax</span>
                      <span className="skill">3D coordinates</span>
                      <span className="skill">Basic shapes</span>
                    </div>
                  </div>
                </div>

                <div className="learning-stage">
                  <div className="stage-number">2</div>
                  <div className="stage-content">
                    <h4>Control</h4>
                    <p>Master printer controls, understand extrusion, and manage layers</p>
                    <div className="stage-skills">
                      <span className="skill">Extrusion control</span>
                      <span className="skill">Speed settings</span>
                      <span className="skill">Layer management</span>
                    </div>
                  </div>
                </div>

                <div className="learning-stage">
                  <div className="stage-number">3</div>
                  <div className="stage-content">
                    <h4>Parametrics</h4>
                    <p>Create designs with variables, loops, and functions</p>
                    <div className="stage-skills">
                      <span className="skill">Variables</span>
                      <span className="skill">Functions</span>
                      <span className="skill">Loops</span>
                    </div>
                  </div>
                </div>

                <div className="learning-stage">
                  <div className="stage-number">4</div>
                  <div className="stage-content">
                    <h4>Advanced</h4>
                    <p>Explore non-planar printing, custom algorithms, and optimization</p>
                    <div className="stage-skills">
                      <span className="skill">Non-planar</span>
                      <span className="skill">Algorithms</span>
                      <span className="skill">Optimization</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="docs-section">
              <h2>Next Steps</h2>
              <p>
                Ready to dive deeper? Choose your next learning path:
              </p>

              <div className="next-steps">
                <Link href="/docs/basics" className="next-step-card">
                  <Lightbulb className="step-icon" />
                  <div>
                    <h4>Learn the Basics</h4>
                    <p>Understand points, steps, and basic FullControl functions</p>
                  </div>
                </Link>
                <Link href="/docs/examples" className="next-step-card">
                  <Wrench className="step-icon" />
                  <div>
                    <h4>Try Examples</h4>
                    <p>Learn by exploring practical, working examples</p>
                  </div>
                </Link>
              </div>
            </section>
          </div>
        </div>
      </div>
    </StandardLayout>
  );
}
